import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolWarning,
} from "../types";

/**
 * Enoxaparin — the syringe whose air bubble is part of the dose.
 *
 * Not a fertility drug, but prescribed through a cycle for thrombophilia,
 * antiphospholipid syndrome and recurrent loss protocols, daily and often for
 * months, so a patient on stimulation meets it on the same evening as a
 * Cetrotide she must expel the bubble from and a ganirelix she may ignore it
 * in. Both labels say the same thing in the same place: **do not push the
 * plunger to get rid of the air bubble, you would lose medicine.** That one
 * sentence is why a coach must be scoped to the device in her hand and never
 * to "how prefilled syringes work".
 *
 * ### Two labels, one procedure
 *
 * The American Lovenox instructions for use (thirteen numbered steps) and the
 * French Lovenox notice (the EU harmonised text, with its three safety-system
 * variants) agree fact for fact: the abdomen at least 5 cm (2 inches) from the
 * navel, out towards the side, alternating; a fold held between finger and
 * thumb for the whole injection; the full needle at 90°; no rubbing; the
 * syringe and its cap to a sharps container. The English copy is the US
 * label's, the French the French notice's.
 *
 * Where they differ, the walk says both: the US syringe fires its sleeve when
 * the plunger is pushed again after withdrawal; the French box carries ERIS,
 * whose sleeve covers the needle on its own as it comes out, or PREVENTIS,
 * which works like the US one. So the step says: if the sleeve does not
 * cover the needle by itself, push the plunger firmly until the click.
 *
 * ### The unit
 *
 * The European box prints "4 000 UI anti-Xa (40 mg)", the American "40 mg".
 * A record in either is her box, and `alsoUnits` lets the dose guard accept
 * both without converting — the one medication here that needed it.
 *
 * ### What the US label does not say to a patient
 *
 * The missed dose and the bleeding warning come from the French notice; the
 * US instructions for use carry neither. The English copy of those two is the
 * author's reading of the French, flagged in the fiche.
 *
 * Restated from `content/data/medications/anticoagulants.json` (the `Clexane`
 * entry), whose text was a disclaimer with no steps until it was written from
 * these two labels.
 */

const ENOXAPARIN_BRANDS = [
  { name: "Lovenox", markets: ["US", "FR"] },
  {
    name: "Clexane",
    markets: ["GB", "EU", "AU", "SG", "MY", "HK", "PH", "TH", "IN"],
  },
] as const satisfies readonly ProtocolBrand[];

/** The French notice's rule; the US instructions for use have none. */
const MISSED_DOSE: ProtocolWarning = {
  id: "enoxaparin.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - If you miss a dose, take it as soon as you realise. Do not inject a double dose on the same day to make up for it.",
    fr: "Dose oubliée - Si vous avez oublié une dose, prenez-la dès que vous vous en rendez compte. Ne vous administrez pas une double dose le même jour pour compenser.",
  },
  sourceSegment:
    "⚠ Missed dose - If you miss a dose, take it as soon as you realise. Do not inject a double dose on the same day to make up for it.",
};

/**
 * Bleeding, to the clinic now. This is an anticoagulant: a bruise at the site
 * is expected, and the leaflet's line is drawn at bleeding that does not stop
 * on its own or the signs of blood loss.
 */
const BLEEDING: ProtocolWarning = {
  id: "enoxaparin.bleeding",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Bleeding - Tell your clinic immediately if you have any bleeding that does not stop by itself, or signs of excessive bleeding such as feeling very weak, tired, pale or dizzy, with a headache or unexplained swelling.",
    fr: "Saignement - Informez votre centre immédiatement si vous présentez le moindre saignement qui ne s'arrête pas de lui-même, ou des signes de saignement excessif comme le fait d'être très faible, fatiguée, pâle ou d'avoir des étourdissements, avec des maux de tête ou un gonflement inexpliqué.",
  },
  sourceSegment:
    "⚠ Bleeding - Tell your clinic immediately if you have any bleeding that does not stop by itself, or signs of excessive bleeding such as feeling very weak, tired, pale or dizzy, with a headache or unexplained swelling.",
};

/** The French notice's "stop and tell a doctor immediately", routed to emergency. */
const ALLERGIC_REACTION: ProtocolWarning = {
  id: "enoxaparin.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - A rash, difficulty breathing or swallowing, or swelling of the face, lips, tongue, mouth, throat or eyes need emergency services straight away.",
    fr: "Aide urgente - Une éruption cutanée, une difficulté à respirer ou à avaler, ou un gonflement du visage, des lèvres, de la langue, de la bouche, de la gorge ou des yeux nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - A rash, difficulty breathing or swallowing, or swelling of the face, lips, tongue, mouth, throat or eyes need emergency services straight away.",
};

export const ENOXAPARIN_PROTOCOL: InjectionProtocol = {
  medicationSlug: "enoxaparin",
  brands: ENOXAPARIN_BRANDS,
  aliases: ["Enoxaparin", "Énoxaparine"],
  indication: "anticoagulant",
  dose: { unit: "mg", alsoUnits: ["IU"] },
  form: "pre_filled_syringe",
  prescriptionForms: ["pre_filled_syringe"],
  route: "subcutaneous",
  version: "2026-10-05.1",
  derivedFrom: "content/data/medications/anticoagulants.json#enoxaparin",
  steps: [
    {
      // "You may see air bubbles: this is normal, do not try to remove them"
      // is said here, before she has the syringe in her hand, and again on
      // the step where the cap comes off. Twice on purpose: it is the one
      // instruction two other syringes in her fridge contradict.
      id: "enoxaparin.syringe.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Gather one prefilled syringe, an alcohol wipe, a cotton ball or gauze and a sharps container, then wash your hands well with soap and water. Check that you have the correct medicine and dose and that the expiry date has not passed. Look at the liquid: it should be clear and colourless to pale yellow; do not use the syringe if it is cloudy, discoloured or contains flakes or particles, or if it has been dropped or damaged. You may see air bubbles: this is normal, do not try to remove them.",
        fr: "Préparer - Rassemblez une seringue préremplie, un tampon alcoolisé, une compresse ou du coton et un collecteur, puis lavez-vous bien les mains à l'eau et au savon. Vérifiez que vous avez le bon médicament et la bonne dose, et que la date de péremption n'est pas dépassée. Regardez le liquide : il doit être limpide et incolore à jaune pâle ; n'utilisez pas la seringue si elle est trouble, colorée ou contient des particules, ou si elle est tombée ou endommagée. Vous pouvez voir des bulles d'air : c'est normal, n'essayez pas de les retirer.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      id: "enoxaparin.syringe.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Choose and clean the site - Inject into the right or left side of your abdomen, at least 2 inches (5 cm) away from your belly button and out towards your side, alternating sides each time. Do not inject into skin that has bruises or scars, and do not inject through clothes. Sit or lie back comfortably so you can see the area, clean it with the alcohol wipe and let your skin dry.",
        fr: "Choisir et désinfecter le site - Injectez dans le côté droit ou gauche de votre abdomen, à au moins 5 cm de votre nombril et vers les côtés, en alternant les côtés à chaque fois. N'injectez pas dans une zone qui présente un bleu ou une cicatrice, et n'injectez pas à travers les vêtements. Asseyez-vous ou allongez-vous confortablement de façon à voir la zone, nettoyez-la avec le tampon alcoolisé et laissez sécher la peau.",
      },
    },
    {
      // `verify` and confirmed: the bubble stays, and a weight-based dose may
      // mean pushing some medicine out first — a look she has to take.
      id: "enoxaparin.syringe.ready-the-syringe",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Ready the syringe - Hold the syringe in the middle with the needle pointing away from you and pull the needle cap straight off; do not twist it, do not put it back on, and do not touch the needle. Do not push the plunger to get rid of the air bubble: this would lose medicine. If your prescribed dose{dose} is less than the amount in the syringe, hold it with the needle pointing down and push the plunger until the amount left matches your dose; otherwise there is nothing to adjust.",
        fr: "Préparer la seringue - Tenez la seringue par le milieu, aiguille vers l'extérieur, et retirez le bouchon de l'aiguille en le tirant tout droit ; ne le tordez pas, ne le remettez pas et ne touchez pas l'aiguille. N'appuyez pas sur le piston pour chasser la bulle d'air : cela ferait perdre du médicament. Si votre dose prescrite{dose} est inférieure à la quantité dans la seringue, tenez-la aiguille vers le bas et poussez le piston jusqu'à ce que la quantité restante corresponde à votre dose ; sinon il n'y a rien à ajuster.",
      },
    },
    {
      id: "enoxaparin.syringe.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Hold the syringe like a pencil with the needle pointing down. With your other hand, pinch the cleaned area of your abdomen between your forefinger and thumb to make a fold of skin, and hold the fold during the entire injection. Insert the full length of the needle straight into the fold at a 90° angle, then push the plunger down slowly and steadily with your thumb until the syringe is empty.",
        fr: "Injecter - Tenez la seringue comme un crayon, aiguille vers le bas. De l'autre main, pincez délicatement la zone nettoyée de votre abdomen entre l'index et le pouce pour faire un pli de peau, et maintenez le pli pendant toute la durée de l'injection. Introduisez toute la longueur de l'aiguille droit dans le pli, à un angle de 90°, puis appuyez sur le piston lentement et régulièrement avec le pouce jusqu'à ce que la seringue soit vide.",
      },
      warnings: [ALLERGIC_REACTION],
    },
    {
      // The two safety systems in one sentence; see the file comment.
      id: "enoxaparin.syringe.remove-the-needle",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Remove the needle - Pull the needle straight out of the skin, keeping your fingers on the plunger, then let go of the skin fold. If the protective sleeve does not cover the needle on its own, point the syringe away from you and others and push the plunger firmly until you hear a click; the sleeve only releases once the syringe has been emptied.",
        fr: "Retirer l'aiguille - Retirez l'aiguille de la peau en la tirant tout droit, les doigts sur le piston, puis lâchez le pli de peau. Si le manchon protecteur ne recouvre pas l'aiguille de lui-même, éloignez la seringue de vous et des autres et appuyez fermement sur le piston jusqu'au clic ; le manchon ne se libère que si la seringue a été entièrement vidée.",
      },
    },
    {
      id: "enoxaparin.syringe.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Do not rub the injection site, to avoid bruising. Put the used syringe and its needle cap in your sharps container right away, never in the household trash. Keep having your injections until your doctor decides to stop them; stopping on your own could let a clot form.",
        fr: "Après l'injection - Ne frottez pas le site d'injection, pour éviter les bleus. Jetez tout de suite la seringue utilisée et le bouchon de l'aiguille dans votre collecteur, jamais avec les ordures ménagères. Continuez vos injections jusqu'à ce que votre médecin décide de les arrêter ; arrêter de vous-même pourrait laisser un caillot se former.",
      },
      warnings: [BLEEDING],
    },
  ],
};
